import Axios, { AxiosError, AxiosResponse } from 'axios'
import { z } from 'zod'
export type { AxiosError }

export const BasePaginateParamsSchema = z.object({
  pageNumber: z.number().optional().default(1),
  pageSize: z.number().optional().default(20),
})

export const BasePaginateResultSchema = z
  .object({
    totalPage: z.number(),
  })
  .merge(BasePaginateParamsSchema)

export const axios = Axios.create({
  baseURL: import.meta.env.VITE_API_URL || 'https://localhost:7040',
  timeout: 50000,
  headers: {},
  withCredentials: true,
})

export type TServerError = {
  statusCode: number
  message: string
  errors?: {
    generalErrors?: string[]
    [key: string]: string[] | undefined
  }
}

export class ServerError extends Error {
  constructor(
    messageOrError: string | TServerError | undefined = 'An error occurred',
    getCustomMessage?: (finalMessage: string) => string
  ) {
    let finalMessage: string

    if (typeof messageOrError === 'string') {
      finalMessage = messageOrError
    } else if (messageOrError && typeof messageOrError === 'object') {
      // Extract generalErrors if available
      const generalErrors = messageOrError.errors?.generalErrors
      if (generalErrors && generalErrors.length > 0) {
        finalMessage = generalErrors.join(' ')
      } else {
        // Fallback to the message property
        finalMessage = messageOrError.message || 'An error occurred'
      }
    } else {
      finalMessage = 'An error occurred'
    }

    if (getCustomMessage) {
      finalMessage = getCustomMessage(finalMessage)
    }
    super(finalMessage)
  }
}

export type ReturnIdRespone = { id: number }

export type ReturnGuIdRespone = { id: string }

export type ValidationField = {
  name: string
  message: string
}
export class ServerValidationError extends Error {
  constructor(public fields: ValidationField[]) {
    super('Validation error')
  }
}

export interface TServerValidationError {
  errors: Record<string, Array<string>>
  message: string
  statusCode: number
}

export function checkForValidationError(
  error: AxiosError<TServerValidationError>
) {
  /**
   * Sample error 400
   * 
      {
        "statusCode": 400,
        "message": "One or more errors occurred!",
        "errors": {
            "generalErrors": [
                "Sum of qty in locations must equals to qty: 200"
            ]
        }
      }
   */
  if (error.response?.status === 400 || error.response?.status === 409) {
    const responseData = error.response.data
    const errors = responseData.errors
    const fields = []
    if (!errors) {
      fields.push({
        name: 'general',
        message: responseData.message || 'One or more errors occurred',
      })
    } else {
      for (const err in errors) {
        if (Object.prototype.hasOwnProperty.call(errors, err)) {
          errors[err].forEach((message) => {
            fields.push({
              name: err,
              message,
            })
          })
        }
      }
    }
    throw new ServerValidationError(fields)
  }
}

export type FileData = {
  fileName: string
  contentType: string
  size: number
  path: string
  url: string
}

export function convertObjectArrayToQueryParam<
  T extends { [key: string]: unknown },
>(arr: T[], field: keyof T): string {
  const strs = arr.map((it) => it[field] as string)
  return convertStringArrayToQueryParam(strs)
}

export function convertStringArrayToQueryParam(arr: string[]): string {
  return '[' + arr.map((it) => `"${it.toString()}"`).join(',') + ']'
}

export function convertStringArrayToIntArrayQueryParam(arr: string[]): string {
  return '[' + arr.map((it) => `${it.toString()}`).join(',') + ']'
}

export function convertDateToYearMonth(date: Date): number {
  return date.getFullYear() * 100 + date.getMonth() + 1
}

export const delay = (ms: number) =>
  new Promise((resolve) => setTimeout(resolve, ms))

export function axiosDownloadAttachment(response: AxiosResponse) {
  const filename = response.headers['content-disposition']
  const filenameRegex = /filename[^;=\n]*=((['"]).*?\2|[^;\n]*)/
  const filenameMatch = filename.match(filenameRegex)
  const filenameValue = filenameMatch
    ? filenameMatch[1].replace(/['"]/g, '')
    : 'downloaded-file.xlsx'

  const url = window.URL.createObjectURL(new Blob([response.data]))
  const link = document.createElement('a')
  link.href = url
  link.setAttribute('download', filenameValue)
  document.body.appendChild(link)
  link.click()
  link.remove()
  window.URL.revokeObjectURL(url)
}
